import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
  type AxiosError,
} from "axios";
import { getKeycloakInstance } from "../auth";

export interface HttpClientConfig {
  baseURL: string;
  tenantSlug: string;
  appCode?: string;
  masterRealm?: boolean;
}

/**
 * Cria instancia Axios configurada com:
 * - Header X-Tenant-Id em cada request.
 * - Bearer token atualizado automaticamente via Keycloak.
 * - Tratamento padrao de 401/403.
 *
 * Port direto de web/src/services/api.ts.
 */
export function createHttpClient(config: HttpClientConfig): AxiosInstance {
  const client = axios.create({
    baseURL: config.baseURL,
  });

  // ── Request interceptor ──
  client.interceptors.request.use(
    async (req: InternalAxiosRequestConfig) => {
      // O console super-admin autentica no realm master, que não é tenant e não
      // deve passar pela composição realmPrefix + slug do backend.
      if (config.masterRealm) {
        req.headers["X-Master-Realm"] = "true";
      } else {
        req.headers["X-Tenant-Id"] = config.tenantSlug;
      }
      if (config.appCode) {
        req.headers["X-App-Code"] = config.appCode;
      }

      // Token refresh + injection
      const kc = getKeycloakInstance();
      if (kc?.authenticated) {
        try {
          await kc.updateToken(30);
        } catch {
          kc.logout({ redirectUri: window.location.origin });
          return Promise.reject(new Error("Sessão expirada"));
        }
      }
      if (kc?.token) {
        req.headers.Authorization = `Bearer ${kc.token}`;
      }

      return req;
    },
  );

  // ── Response interceptor ──
  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const status = error.response?.status;

      if (status === 401) {
        const kc = getKeycloakInstance();
        kc?.logout({ redirectUri: window.location.origin });
      }

      return Promise.reject(error);
    },
  );

  return client;
}
